import { useCallback, useEffect, useState } from 'react';
import {
  applyTheme,
  readStoredTheme,
  resolveTheme,
  writeStoredTheme,
  type ResolvedTheme,
  type ThemePref,
} from '../lib/theme';

const THEME_CYCLE: ThemePref[] = ['system', 'dark', 'light'];

export function useTheme() {
  const [theme, setThemeState] = useState<ThemePref>(() => readStoredTheme());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readStoredTheme()));

  // Re-resolve when OS color-scheme changes (only matters when pref === 'system')
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (theme === 'system') {
        setResolved(applyTheme('system'));
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((next: ThemePref) => {
    writeStoredTheme(next);
    const r = applyTheme(next);
    setThemeState(next);
    setResolved(r);
  }, []);

  const cycleTheme = useCallback(() => {
    const current = readStoredTheme();
    const idx = THEME_CYCLE.indexOf(current);
    const nextIdx = (idx + 1) % THEME_CYCLE.length;
    const next = THEME_CYCLE[nextIdx] ?? 'system';
    setTheme(next);
  }, [setTheme]);

  return { theme, resolvedTheme: resolved, setTheme, cycleTheme };
}
